import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        info: 'bg-info text-info-foreground shadow-sm hover:bg-info/90',
        'info-outline':
          'border border-info bg-background text-info shadow-xs hover:bg-info hover:text-info-foreground',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        'destructive-outline':
          'border border-destructive text-destructive bg-background shadow-xs hover:bg-destructive hover:text-destructive-foreground',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        'success-outline':
          'border border-success bg-background shadow-xs text-success hover:bg-success hover:text-success-foreground',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-foreground underline-offset-4 hover:underline',
        warning:
          'bg-warning text-warning-foreground shadow-xs hover:bg-warning/90',
        success:
          'bg-success text-success-foreground shadow-xs hover:bg-success/80',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'md-icon': 'size-8',
        'sm-icon': 'size-7',
        'lg-icon': 'size-12',
      },
      active: {
        true: 'brightness-90',
        false: '',
      },
      invalid: {
        true: 'border border-red-500! shadow-red-500! shadow-[0_0px_5px_0_var(--shadow-color),0_0px_5px_-1px_var(--shadow-color)]!',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      active: false,
      invalid: false,
    },
  }
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = ({
  className,
  variant,
  size,
  active,
  invalid,
  asChild = false,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, active, invalid, className })
      )}
      type="button"
      {...props}
    />
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
